import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, MapPin, Phone, Globe, Mail, Clock, Navigation, BadgeCheck, Package, Building2, ExternalLink, Search } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { RatingBlock } from "@/components/Stars";
import { Reviews } from "@/components/Reviews";
import { ResultsMap } from "@/components/ResultsMap";
import { TopRated, isTopRated } from "@/components/BusinessCard";
import { SuggestPhone } from "@/components/SuggestPhone";

const Section = ({ title, children, testid }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={testid}>
    <h2 className="font-display text-xl font-bold">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

function FindPhone({ b }) {
  const where = [b.postal_code, b.city].filter(Boolean).join(" ");
  const google = `https://www.google.com/search?q=${encodeURIComponent(`${b.name} ${where} téléphone`)}`;
  const pj = `https://www.pagesjaunes.fr/annuaire/chercherlespros?quoiqui=${encodeURIComponent(b.name || "")}&ou=${encodeURIComponent(where)}`;
  const btn = "flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-[#1E40AF] hover:text-[#1E40AF]";
  return (
    <div className="space-y-2 rounded-lg bg-slate-50 p-3" data-testid="find-phone-box">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Phone className="h-3.5 w-3.5" />Numéro non renseigné — chercher le numéro :</p>
      <div className="grid grid-cols-2 gap-2">
        <a href={google} target="_blank" rel="noreferrer" className={btn} data-testid="find-phone-google"><Search className="h-3.5 w-3.5" />Sur Google</a>
        <a href={pj} target="_blank" rel="noreferrer" className={btn} data-testid="find-phone-pagesjaunes"><Search className="h-3.5 w-3.5" />Sur Pages Jaunes</a>
      </div>
      {!b.claimed && b.siret && (
        <Link to={`/mon-espace?siret=${b.siret}`} className="block pt-1 text-center text-xs font-semibold text-[#1E40AF] hover:underline" data-testid="add-phone-claim-link">
          Vous êtes ce pro ? Ajoutez votre numéro gratuitement
        </Link>
      )}
      <SuggestPhone b={b} />
    </div>
  );
}

function Contact({ b }) {
  const row = (Icon, content, testid) => content && <p className="flex items-start gap-3 text-sm text-slate-700" data-testid={testid}><Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1E40AF]" />{content}</p>;
  const site = b.website && <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer" className="break-all text-[#1E40AF] hover:underline">{b.website}</a>;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="contact-box">
      {row(MapPin, b.address, "contact-address")}
      {row(Phone, b.phone && <a href={`tel:${b.phone}`} className="font-semibold hover:text-[#1E40AF]">{b.phone}</a>, "contact-phone")}
      {row(Mail, b.email && <a href={`mailto:${b.email}`} className="hover:text-[#1E40AF]">{b.email}</a>, "contact-email")}
      {row(Globe, site, "contact-website")}
      {row(Clock, b.hours && <span className="whitespace-pre-line">{b.hours}</span>, "contact-hours")}
      {b.contact_source === "osm" && <p className="text-[11px] text-slate-400" data-testid="contact-osm-source">Coordonnées : © contributeurs OpenStreetMap</p>}
      {b.contact_source === "community" && <p className="text-[11px] text-slate-400" data-testid="contact-community-source">{b.contact_thanks ? `Merci à ${b.contact_thanks} pour ce numéro` : "Numéro proposé par un utilisateur et vérifié"}</p>}
      {!b.phone && <FindPhone b={b} />}
      {b.lat != null && (
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lon}`} target="_blank" rel="noreferrer" className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-[#1E40AF]" data-testid="itinerary-button">
          <Navigation className="h-4 w-4" />Itinéraire
        </a>
      )}
      {b.google_maps_uri && <a href={b.google_maps_uri} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-[#1E40AF]"><ExternalLink className="h-3 w-3" />Voir sur Google Maps</a>}
    </div>
  );
}

function Official({ b }) {
  const items = [["SIRET", b.siret], ["SIREN", b.siren], ["Raison sociale", b.legal_name], ["Code NAF/APE", b.naf], ["Création", b.creation_date && new Date(b.creation_date).toLocaleDateString("fr-FR")]];
  if (!b.siret) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="official-data">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500"><Building2 className="h-4 w-4" />Données officielles SIRENE</p>
      <dl className="mt-3 space-y-2 text-sm">
        {items.filter(([, v]) => v).map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="font-mono-id text-right text-slate-800">{v}</dd></div>)}
      </dl>
    </div>
  );
}

function Tags({ items, testid }) {
  return <div className="flex flex-wrap gap-2" data-testid={testid}>{items.map((t) => <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-700">{t}</span>)}</div>;
}

function Products({ items }) {
  return (
    <ul className="divide-y divide-slate-100" data-testid="products-list">
      {items.map((p) => (
        <li key={p.id} className="flex items-center justify-between gap-3 py-3 text-sm">
          <span><b className="text-slate-900">{p.name}</b>{p.brand && <span className="text-slate-500"> · {p.brand}</span>}</span>
          <span className="flex items-center gap-3">
            {p.price != null && <span className="font-semibold">{p.price.toFixed(2)} €</span>}
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${p.in_stock ? "bg-[#DCFCE7] text-[#14532D]" : "bg-slate-100 text-slate-500"}`}>{p.in_stock ? "En stock" : "Sur commande"}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Hero({ b }) {
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap gap-2">
          {isTopRated(b) && <TopRated large />}
          {b.claimed ? <span className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-semibold text-[#14532D]" data-testid="claim-status-claimed"><BadgeCheck className="h-3.5 w-3.5" />Fiche gérée par le professionnel</span>
            : <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="claim-status-unclaimed">Fiche non revendiquée</span>}
          {b.in_stock_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-3 py-1 text-xs font-semibold text-[#78350F]"><Package className="h-3.5 w-3.5" />{b.in_stock_count} produit(s) en stock</span>}
        </div>
        <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" data-testid="business-name">{b.name}</h1>
        <p className="mt-2 text-slate-600">{[b.naf_label || (b.naf ? `Code NAF ${b.naf}` : b.source === "google" ? "Fiche Google" : null), b.city].filter(Boolean).join(" · ")}</p>
        <div className="mt-4"><RatingBlock b={b} /></div>
        {(b.phone || b.email) && (
          <div className={`mt-6 grid gap-2 lg:hidden ${b.phone && b.email ? "grid-cols-[1fr_auto]" : "grid-cols-1"}`}>
            {b.phone && (
              <a href={`tel:${b.phone.replace(/[^\d+]/g, "")}`} className="flex items-center justify-center gap-2 rounded-xl bg-[#166534] py-4 text-lg font-bold text-white shadow-lg transition-colors hover:bg-[#14532D]" data-testid="call-button">
                <Phone className="h-5 w-5" />Appeler{b.email ? "" : ` · ${b.phone}`}
              </a>
            )}
            {b.email && (
              <a href={`mailto:${b.email}?subject=${encodeURIComponent(`Demande via Qui Fait Quoi`)}`} className="flex items-center justify-center gap-2 rounded-xl bg-[#1E40AF] px-6 py-4 text-lg font-bold text-white shadow-lg transition-colors hover:bg-[#1D4ED8]" data-testid="write-button">
                <Mail className="h-5 w-5" />Écrire
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BusinessPage() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const reload = () => api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch(() => {});
  useEffect(() => {
    setB(null);
    api.get(`/businesses/${id}`).then((r) => setB(r.data)).catch((e) => setError(errMsg(e)));
  }, [id]);
  useEffect(() => {
    if (b && window.location.search.includes("noter=1")) {
      setTimeout(() => document.getElementById("avis")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [b?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <p className="py-32 text-center text-red-600" data-testid="business-error">{error}</p>;
  if (!b) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div data-testid="business-page">
      <Hero b={b} />
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_360px] lg:px-8">
        <div className="space-y-6">
          {b.photos?.length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="photo-gallery">
              {b.photos.map((p, i) => <a key={p.id} href={fileUrl(p.url)} target="_blank" rel="noreferrer" className={`overflow-hidden rounded-xl ${b.photos.length === 1 ? "col-span-2 sm:col-span-3 max-h-[420px]" : i === 0 ? "col-span-2 row-span-2" : ""}`}><img src={fileUrl(p.url)} alt="" className="h-full min-h-[140px] w-full object-cover transition-transform duration-300 hover:scale-105" /></a>)}
            </div>
          )}
          {b.description && <Section title="Présentation" testid="description-section"><p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">{b.description}</p></Section>}
          {b.skills?.length > 0 && <Section title="Compétences" testid="skills-section"><Tags items={b.skills} testid="skills-list" /></Section>}
          {b.brands?.length > 0 && <Section title="Marques représentées" testid="brands-section"><Tags items={b.brands} testid="brands-list" /></Section>}
          {b.products?.length > 0 && <Section title="Produits & stock" testid="products-section"><Products items={b.products} /></Section>}
          {!b.claimed && b.siret && (
            <div className="flex flex-col items-start justify-between gap-4 rounded-xl bg-slate-900 p-6 text-white sm:flex-row sm:items-center">
              <p className="font-semibold">C'est votre entreprise ? Complétez votre fiche : marques, produits, photos…</p>
              <Link to={`/mon-espace?siret=${b.siret}`} className="shrink-0 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-[#D97706] hover:text-white" data-testid="claim-business-button">Revendiquer cette fiche</Link>
            </div>
          )}
          <Reviews b={b} onChange={reload} />
        </div>
        <aside className="order-first space-y-4 lg:order-none lg:sticky lg:top-20 lg:self-start">
          <Contact b={b} />
          {b.lat != null && <ResultsMap items={[b]} height="220px" />}
          <Official b={b} />
        </aside>
      </div>
    </div>
  );
}
